import { useEffect, useState, useRef, useCallback } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Spinner, Select, SelectItem, Button, Chip, Input, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Checkbox } from "@heroui/react";
import { 
  FireExtinguisher,
  Lightbulb,
  Calendar,
  Warning,
  Plus,
  Check,
  X,
  PencilSimple,
  FilePdf,
  MagnifyingGlass,
  Copy,
  Question,
  Clock
} from "@phosphor-icons/react";
import ModalUploadFileLog, { FileUploadField } from "shared/components/uploadFileModal/uploadFileModal";
import { FileLabel, FileLogStatus } from "shared/types/file";
import { ClimatisationSource, HeatingConnectedTo } from 'shared/types/climatisation';

interface ClimatisationUnit {
  id: string;
  building: {
    id: string;
    location: string;
  };
  unit: {
    id: string;
    boxNumber: string;
    floor: number;
  };
  climatisationInfo: {
    id?: number;
    connectedTo: string | null;
    source: string | null;
    remarks?: string;
  } | null;
  devices: Array<{
    id: number;
    deviceType: string;
    deviceSubType: string;
    brand?: string;
    model?: string;
    drives: string;
    deviceLocation?: string;
    installationDate?: string;
          maintenanceAttests: Array<{
      id: number;
      label: string[];
      documentDate?: string;
      expirationDate?: string;
      status: string;
      statusDescription?: string;
      sharePointFileUrl?: string;
      createdAt?: string;
      isBuildingLevel?: boolean;
    }>;
  }>;
  maintenanceAttests: Array<{
    id: number;
    label: string[];
    documentDate?: string;
    expirationDate?: string;
    status: string;
    statusDescription?: string;
    isBuildingLevel?: boolean;
    sharePointFileUrl?: string;
    createdAt?: string;
  }>;
  status: 'valid' | 'warning' | 'expired' | 'missing' | 'not_required' | 'unknown';
  lastInspectionDate?: string;
  nextInspectionDate?: string;
}

interface ActionCard {
  title: string;
  count: number;
  color: string;
  filterType: string;
  description: string;
}

interface Filters {
  deviceType: string;
  deviceEnergySource: string;
  deviceDrives: string;
  climatisationConnectedTo: string;
  climatisationSource: string;
  maintenanceStatus: string;
}

const ClimatisationSection = () => {
  const [units, setUnits] = useState<ClimatisationUnit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<ClimatisationUnit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeActionCard, setActiveActionCard] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    deviceType: '',
    deviceEnergySource: '',
    deviceDrives: '',
    climatisationConnectedTo: '',
    climatisationSource: '',
    maintenanceStatus: ''
  });
  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<ClimatisationUnit | null>(null);
  // Editing state
  const [editingCell, setEditingCell] = useState<{unitId: string, field: string} | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  
  // Sticky header state
  const tableRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [tableLeft, setTableLeft] = useState<number>(0);

  // Copy functionality state
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [sourceUnit, setSourceUnit] = useState<ClimatisationUnit | null>(null);
  const [sourceField, setSourceField] = useState<string>('');
  const [sourceFieldLabel, setSourceFieldLabel] = useState<string>('');
  const [selectedTargetUnits, setSelectedTargetUnits] = useState<string[]>([]);
  const [isCopying, setIsCopying] = useState(false);

  // History modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyUnit, setHistoryUnit] = useState<ClimatisationUnit | null>(null);

  const { session } = useSession();

  // Calculate column dimensions for sticky header
  const calculateColumnDimensions = useCallback(() => {
    if (!tableRef.current) return;
    
    const table = tableRef.current.querySelector('table');
    if (!table) return;
    
    const rect = table.getBoundingClientRect();
    setTableWidth(rect.width);
    setTableLeft(rect.left);
    
    const headerCells = table.querySelectorAll('thead th');
    const widths = Array.from(headerCells).map(cell => cell.getBoundingClientRect().width);
    setColumnWidths(widths);
  }, []);

  const fetchClimatisationData = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/maintenance/climatisation-devices`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      
      // Sort alphabetically by unit ID by default
      const sortedData = data.sort((a: ClimatisationUnit, b: ClimatisationUnit) => 
        a.unit.id.localeCompare(b.unit.id)
      );
      
      setUnits(sortedData);
      setFilteredUnits(sortedData);
    } catch (error) {
      console.error("Fout bij ophalen verwarmingsgegevens:", error);
    }
  }, [session]);

  useEffect(() => {
    fetchClimatisationData();

    // Set up scroll handler for sticky header
    const handleScroll = () => {
      if (!tableRef.current) return;
      
      const tableTop = tableRef.current.getBoundingClientRect().top;
      const headerHeight = 70; // Adjust based on your header height
      
      setIsHeaderSticky(tableTop < headerHeight);
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', calculateColumnDimensions);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateColumnDimensions);
    };
  }, [session, calculateColumnDimensions, fetchClimatisationData]);

  // Initial data fetch with loading state
  useEffect(() => {
    const initialFetch = async () => {
      setIsLoading(true);
      await fetchClimatisationData();
      setIsLoading(false);
    };
    
    initialFetch();
  }, [fetchClimatisationData]);

  // Calculate column widths when data changes
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateColumnDimensions();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [filteredUnits, calculateColumnDimensions]);

  const getDeviceEnergySource = (unit: ClimatisationUnit) => {
    if (!unit.climatisationInfo?.source) {
      return 'Onbekend';
    }
    
    const gasKettles = [
      ClimatisationSource.GAS_KETTLE,
      ClimatisationSource.GAS_KETTLE_CONDENSEREND,
      ClimatisationSource.GAS_KETTLE_HR
    ];
    
    if (gasKettles.includes(unit.climatisationInfo.source as ClimatisationSource)) {
      return 'Gas';
    }
    
    if (unit.climatisationInfo.source.startsWith('Elektriciteit')) {
      return 'Elektriciteit';
    }
    
    if (unit.climatisationInfo.source === ClimatisationSource.HYBRID) {
      return 'Hybride';
    }
    
    return 'Onbekend';
  };

  // Apply filters and search whenever they change
  useEffect(() => {
    let filtered = units;

    // Apply search filter first
    if (searchQuery.trim()) {
      filtered = filtered.filter(unit => 
        unit.unit.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        unit.unit.boxNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        unit.building.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Handle action card filters
    if (activeActionCard === 'gas_no_valid_attest') {
      filtered = filtered.filter(unit => {
        const energySource = getDeviceEnergySource(unit);
        const isGasSystem = energySource === 'Gas' || energySource === 'Hybride';
        
        if (!isGasSystem) return false;
        
        return unit.status === 'missing' || unit.status === 'expired';
      });
    } else if (activeActionCard === 'gas') {
      filtered = filtered.filter(unit => {
        const energySource = getDeviceEnergySource(unit);
        return energySource === 'Gas' || energySource === 'Hybride';
      });
    } else if (activeActionCard === 'electricity') {
      filtered = filtered.filter(unit => {
        const energySource = getDeviceEnergySource(unit);
        return energySource === 'Elektriciteit';
      });
    } else if (activeActionCard === 'missing_connection_info') {
      filtered = filtered.filter(unit => 
        !unit.climatisationInfo?.connectedTo || unit.climatisationInfo.connectedTo === HeatingConnectedTo.UNKNOWN
      );
    } else if (activeActionCard === 'missing_energy_source_info') {
      filtered = filtered.filter(unit => 
        !unit.climatisationInfo?.source || unit.climatisationInfo.source === ClimatisationSource.UNKNOWN
      );
    } else if (activeActionCard === 'expired') {
      filtered = filtered.filter(unit => unit.status === 'expired');
    } else if (activeActionCard === 'warning') {
      filtered = filtered.filter(unit => unit.status === 'warning');
    } else if (!activeActionCard) {
      // Apply manual filters when no action card is active
      if (filters.deviceType) {
        filtered = filtered.filter(unit => 
          unit.devices.some(device => device.deviceType === filters.deviceType) ||
          (filters.deviceType === 'Opwekkingstoestel' && 
           (unit.climatisationInfo?.connectedTo === 'Centraal systeem' || 
            unit.climatisationInfo?.connectedTo === 'Combinatie privatief-collectief'))
        );
      }

      if (filters.deviceEnergySource) {
        filtered = filtered.filter(unit => {
          const energySource = getDeviceEnergySource(unit);
          return energySource === filters.deviceEnergySource;
        });
      }

      if (filters.deviceDrives) {
        filtered = filtered.filter(unit => 
          unit.devices.some(device => device.drives === filters.deviceDrives)
        );
      }

      if (filters.climatisationConnectedTo) {
        filtered = filtered.filter(unit => 
          unit.climatisationInfo?.connectedTo === filters.climatisationConnectedTo
        );
      }

      if (filters.climatisationSource) {
        if (filters.climatisationSource === 'Gas') {
          filtered = filtered.filter(unit => 
            unit.climatisationInfo?.source && 
            (unit.climatisationInfo.source.includes('Gas') || unit.climatisationInfo.source === 'Hybride')
          );
        } else if (filters.climatisationSource === 'Elektriciteit') {
          filtered = filtered.filter(unit => 
            unit.climatisationInfo?.source && 
            unit.climatisationInfo.source.includes('Elektriciteit')
          );
        }
      }

      if (filters.maintenanceStatus) {
        filtered = filtered.filter(unit => unit.status === filters.maintenanceStatus);
      }
    }

    setFilteredUnits(filtered);
  }, [filters, units, activeActionCard, searchQuery]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid':
        return '#5B7304'; // moss-primary
      case 'warning':
        return '#EA8162'; // brick-primary
      case 'expired':
        return '#EA8162'; // brick-primary
      case 'missing':
        return '#EA8162'; // brick-primary
      case 'not_required':
        return '#838C89'; // neutral-secondaryMid
      case 'unknown':
        return '#838C89'; // neutral-secondaryMid
      default:
        return '#838C89'; // neutral-secondaryMid
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      // Internal status values (used by worst-case logic)
      case 'valid': return 'Conform';
      case 'warning': return 'Aandacht vereist';
      case 'expired': return 'Niet conform';
      case 'missing': return 'Ontbreekt';
      case 'not_required': return 'Niet vereist';
      case 'unknown': return 'Onbekend';
      // FileLogStatus values (for backwards compatibility)
      case FileLogStatus.OK: return 'Conform';
      case FileLogStatus.OK_WITH_REMARKS: return 'Conform, met opmerkingen';
      case FileLogStatus.NOT_REQUIRED: return 'Niet vereist';
      case FileLogStatus.NOT_OK: return 'Niet conform';
      case FileLogStatus.UNKNOWN: return 'Onbekend';
      default: return 'Onbekend';
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-BE');
  };

  const getConnectionTypeText = (connectedTo?: string | null | undefined) => {
    if (!connectedTo) return 'Onbekend';
    return connectedTo;
  };

  const getSourceText = (source?: string | null | undefined) => {
    if (!source) return 'Onbekend';
    return source;
  };

  const getDevicesByType = (unit: ClimatisationUnit, deviceType: string) => {
    if (unit.devices.length === 0) {
      if (unit.climatisationInfo?.connectedTo === 'Centraal systeem' || 
          unit.climatisationInfo?.connectedTo === 'Combinatie privatief-collectief') {
        return deviceType === 'Opwekkingstoestel' ? 'Centraal systeem' : '-';
      }
      return '-';
    }

    const devicesOfType = unit.devices.filter(device => device.deviceType === deviceType);
    
    if (devicesOfType.length === 0) return '-';

    return devicesOfType.map(device => {
      const parts = [];
      if (device.deviceSubType && device.deviceSubType !== 'Onbekend') {
        parts.push(device.deviceSubType);
      }
      if (device.brand) {
        parts.push(device.brand);
      }
      if (device.model) {
        parts.push(device.model);
      }
      return parts.length > 0 ? parts.join(' - ') : device.deviceType;
    }).join(', ');
  };

  const getMaintenanceInfo = (unit: ClimatisationUnit) => {
    const allAttests = [...unit.maintenanceAttests, ...unit.devices.flatMap(d => d.maintenanceAttests)];
    
    if (allAttests.length === 0) {
      const energySource = getDeviceEnergySource(unit);
      
      // Check if energy source is unknown
      if (energySource === 'Onbekend') {
        return {
          status: 'unknown',
          text: 'Afhankelijk van energiebron',
          documentDate: null,
          expirationDate: null,
          sharePointFileUrl: null
        };
      }
      
      // Check if maintenance is required
      const requiresAttest = unit.climatisationInfo?.source && 
        (unit.climatisationInfo.source.includes('Gas') || 
         unit.climatisationInfo.source === 'Hybride');
      
      if (requiresAttest) {
        return {
          status: 'missing',
          text: 'Onderhoudsattest ontbreekt',
          documentDate: null,
          expirationDate: null,
          sharePointFileUrl: null
        };
      } else {
        return {
          status: 'not_required',
          text: 'Geen onderhoud vereist',
          documentDate: null,
          expirationDate: null,
          sharePointFileUrl: null
        };
      }
    }

    // Find most recent attest by upload date (createdAt) - this is the ONLY one we should consider for status and expiration
    const validAttests = allAttests.filter(a => a.createdAt || a.documentDate);
    const mostRecent = validAttests.length > 0 ? 
      validAttests.sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : (a.documentDate ? new Date(a.documentDate).getTime() : 0);
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : (b.documentDate ? new Date(b.documentDate).getTime() : 0);
        return bDate - aDate;
      })[0] : null;

    // Use the most recent attest for everything (status, expiration, etc.)
    if (mostRecent) {
      // Use the new worst-case status logic for consistency with history modal
      const overallStatus = getOverallWorstCaseStatus(mostRecent.status, mostRecent.expirationDate);
      
      return {
        status: overallStatus.status,
        text: overallStatus.text,
        documentDate: mostRecent.documentDate,
        expirationDate: mostRecent.expirationDate,
        sharePointFileUrl: mostRecent.sharePointFileUrl
      };
    }

    // Fallback to unit.status if no attests found
    return {
      status: unit.status,
      text: getStatusText(unit.status),
      documentDate: null,
      expirationDate: null,
      sharePointFileUrl: null
    };
  };

  const getActionCards = (): ActionCard[] => {
    const gasUnits = units.filter(unit => {
      const energySource = getDeviceEnergySource(unit);
      return energySource === 'Gas' || energySource === 'Hybride';
    });

    const electricityUnits = units.filter(unit => {
      const energySource = getDeviceEnergySource(unit);
      return energySource === 'Elektriciteit';
    });

    const expiredUnits = units.filter(unit => unit.status === 'expired');
    const warningUnits = units.filter(unit => unit.status === 'warning');

    // Gas systems without valid maintenance certificates
    const gasUnitsWithoutValidAttest = units.filter(unit => {
      const energySource = getDeviceEnergySource(unit);
      const isGasSystem = energySource === 'Gas' || energySource === 'Hybride';
      
      if (!isGasSystem) return false;
      
      // For gas systems, check if they have missing or expired attests
      return unit.status === 'missing' || unit.status === 'expired';
    });

    // Units missing connection info
    const unitsWithoutConnectionInfo = units.filter(unit => 
      !unit.climatisationInfo?.connectedTo || unit.climatisationInfo.connectedTo === 'Onbekend'
    );

    // Units missing energy source info
    const unitsWithoutEnergySourceInfo = units.filter(unit => 
      !unit.climatisationInfo?.source || unit.climatisationInfo.source === 'Onbekend'
    );

    return [
      {
        title: 'Gas systemen',
        count: gasUnits.length,
        color: '#EA8162',
        filterType: 'gas',
        description: 'Units met gas-gedreven verwarming'
      },
      {
        title: 'Elektrische systemen',
        count: electricityUnits.length,
        color: '#5B7304',
        filterType: 'electricity',
        description: 'Units met elektrische verwarming'
      },
      {
        title: 'Gas zonder geldig attest',
        count: gasUnitsWithoutValidAttest.length,
        color: '#EA8162',
        filterType: 'gas_no_valid_attest',
        description: 'Gas systemen zonder geldig onderhoudsattest'
      },
      {
        title: 'Ontbrekende info over aansluiting',
        count: unitsWithoutConnectionInfo.length,
        color: '#838C89',
        filterType: 'missing_connection_info',
        description: 'Units zonder aansluitingsinformatie'
      },
      {
        title: 'Ontbrekende info over energiebron',
        count: unitsWithoutEnergySourceInfo.length,
        color: '#838C89',
        filterType: 'missing_energy_source_info',
        description: 'Units zonder energiebroninformatie'
      },
      {
        title: 'Verlopen keuringen',
        count: expiredUnits.length,
        color: '#EA8162',
        filterType: 'expired',
        description: 'Onderhoudsattesten verlopen'
      },
      {
        title: 'Binnenkort vervallen',
        count: warningUnits.length,
        color: '#EA8162',
        filterType: 'warning',
        description: 'Vervallen binnen 3 maanden'
      }
    ];
  };

  const handleActionCardClick = (filterType: string) => {
    if (activeActionCard === filterType) {
      // Remove filter if clicking the same one
      setActiveActionCard(null);
      setFilters({
        deviceType: '',
        deviceEnergySource: '',
        deviceDrives: '',
        climatisationConnectedTo: '',
        climatisationSource: '',
        maintenanceStatus: ''
      });
      return;
    }

    // Set the active action card - the applyFilters function will handle the filtering
    setActiveActionCard(filterType);
    
    // Clear manual filters when action card is selected
    setFilters({
      deviceType: '',
      deviceEnergySource: '',
      deviceDrives: '',
      climatisationConnectedTo: '',
      climatisationSource: '',
      maintenanceStatus: ''
    });
  };

  const handleFilterChange = (filterKey: keyof Filters, value: string) => {
    setActiveActionCard(null); // Clear action card selection when manual filter is applied
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearAllFilters = () => {
    setActiveActionCard(null);
    setSearchQuery('');
    setFilters({
      deviceType: '',
      deviceEnergySource: '',
      deviceDrives: '',
      climatisationConnectedTo: '',
      climatisationSource: '',
      maintenanceStatus: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(filter => filter !== '') || searchQuery.trim() !== '';

  // Get unique values for filter options
  const getUniqueDeviceTypes = () => {
    const types = new Set<string>();
    units.forEach(unit => {
      unit.devices.forEach(device => types.add(device.deviceType));
    });
    return Array.from(types).sort();
  };

  const getUniqueDeviceDrives = () => {
    const drives = new Set<string>();
    units.forEach(unit => {
      unit.devices.forEach(device => {
        if (device.drives && device.drives !== 'NVT') {
          drives.add(device.drives);
        }
      });
    });
    return Array.from(drives).sort();
  };

  const getUniqueConnectionTypes = () => {
    const types = new Set<string>();
    units.forEach(unit => {
      if (unit.climatisationInfo?.connectedTo) {
        types.add(unit.climatisationInfo.connectedTo);
      }
    });
    return Array.from(types).sort();
  };

  const handleUploadAttest = (unit: ClimatisationUnit) => {
    setSelectedUnit(unit);
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedUnit(null);
  };

  const handleFileUploadSuccess = async () => {
    // Refresh the data after successful upload
    try {
      const response = await fetchWithAuth(
        `${API_URL}/maintenance/climatisation-devices`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setUnits(data);
      setFilteredUnits(data);
    } catch (error) {
      console.error("Fout bij ophalen verwarmingsgegevens:", error);
    }
    handleCloseUploadModal();
  };

  const getUploadLabel = (isCollective: boolean): FileLabel => {
    return isCollective 
      ? FileLabel.CollectieveVerwarmingOnderhoudsattest 
      : FileLabel.VerwarmingOnderhoudsattest;
  };

  const shouldShowUploadButton = (unit: ClimatisationUnit) => {
    // Check if we have both connection and energy source info
    const hasConnectionInfo = unit.climatisationInfo?.connectedTo && unit.climatisationInfo.connectedTo !== 'Onbekend';
    const hasEnergySourceInfo = unit.climatisationInfo?.source && unit.climatisationInfo.source !== 'Onbekend';
    
    if (!hasConnectionInfo || !hasEnergySourceInfo) {
      return false; // Don't show upload button if missing basic info
    }

    const energySource = getDeviceEnergySource(unit);
    const isGasSystem = energySource === 'Gas' || energySource === 'Hybride';
    const needsAttest = unit.status === 'missing' || unit.status === 'expired';
    
    return isGasSystem && needsAttest;
  };

  const handleEditStart = (unitId: string, field: string, currentValue: string) => {
    setEditingCell({ unitId, field });
    setEditingValue(currentValue || '');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleEditSave = async () => {
    if (!editingCell) return;

    try {
      const unit = units.find(u => u.id === editingCell.unitId);
      if (!unit) return;

      // Prepare the update payload with only the field being updated
      const updatePayload: any = {
        unitId: unit.unit.id,
        buildingId: unit.building.id,
        [editingCell.field]: editingValue
      };

      // Only include id if climatisationInfo exists and has an id
      if (unit.climatisationInfo?.id) {
        updatePayload.id = unit.climatisationInfo.id;
      }

      // Update local state immediately for instant UI feedback
      const updatedClimatisationInfo = {
        id: unit.climatisationInfo?.id,
        connectedTo: editingCell.field === 'connectedTo' ? (editingValue || null) : (unit.climatisationInfo?.connectedTo || null),
        source: editingCell.field === 'source' ? (editingValue || null) : (unit.climatisationInfo?.source || null),
        remarks: editingCell.field === 'remarks' ? editingValue : unit.climatisationInfo?.remarks
      };

      setUnits(prevUnits => 
        prevUnits.map(u => 
          u.id === editingCell.unitId 
            ? { ...u, climatisationInfo: updatedClimatisationInfo }
            : u
        )
      );
      
      setFilteredUnits(prevUnits => 
        prevUnits.map(u => 
          u.id === editingCell.unitId 
            ? { ...u, climatisationInfo: updatedClimatisationInfo }
            : u
        )
      );

      const response = await fetchWithAuth(
        `${API_URL}/climatisation/info`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        },
        session
      );

      if (response.ok) {
        const savedClimatisationInfo = await response.json();
        
        // Update the local state with the saved climatisation info (including any new ID)
        setUnits(prevUnits => 
          prevUnits.map(u => 
            u.id === editingCell.unitId 
              ? {
                  ...u,
                  climatisationInfo: {
                    id: savedClimatisationInfo.id,
                    connectedTo: savedClimatisationInfo.connectedTo,
                    source: savedClimatisationInfo.source,
                    remarks: savedClimatisationInfo.remarks
                  }
                }
              : u
          )
        );
        
        // Also update filtered units
        setFilteredUnits(prevUnits => 
          prevUnits.map(u => 
            u.id === editingCell.unitId 
              ? {
                  ...u,
                  climatisationInfo: {
                    id: savedClimatisationInfo.id,
                    connectedTo: savedClimatisationInfo.connectedTo,
                    source: savedClimatisationInfo.source,
                    remarks: savedClimatisationInfo.remarks
                  }
                }
              : u
          )
        );
        
        setEditingCell(null);
        setEditingValue('');
      } else {
        console.error("Fout bij opslaan:", response.statusText);
        // Revert local state on error
        await fetchClimatisationData();
      }
    } catch (error) {
      console.error("Fout bij opslaan:", error);
      // Revert local state on error
      await fetchClimatisationData();
    }
  };

  const handleCopyField = (unit: ClimatisationUnit, field: string, fieldLabel: string) => {
    setSourceUnit(unit);
    setSourceField(field);
    setSourceFieldLabel(fieldLabel);
    setSelectedTargetUnits([]);
    setCopyModalOpen(true);
  };

  const copyFieldToSelectedTargets = async () => {
    if (!sourceUnit || !sourceField || selectedTargetUnits.length === 0) return;

    setIsCopying(true);
    try {
      const sourceValue = sourceUnit.climatisationInfo?.[sourceField as keyof typeof sourceUnit.climatisationInfo];
      
      for (const targetUnitId of selectedTargetUnits) {
        const targetUnit = units.find(u => u.id === targetUnitId);
        if (!targetUnit) continue;

        const updatePayload: any = {
          unitId: targetUnit.unit.id,
          buildingId: targetUnit.building.id,
          [sourceField]: sourceValue
        };

        if (targetUnit.climatisationInfo?.id) {
          updatePayload.id = targetUnit.climatisationInfo.id;
        }

        const response = await fetchWithAuth(
          `${API_URL}/climatisation/info`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload),
          },
          session
        );

        if (response.ok) {
          const savedClimatisationInfo = await response.json();
          
          // Update the local state with the saved climatisation info
          setUnits(prevUnits => 
            prevUnits.map(u => 
              u.id === targetUnitId 
                ? {
                    ...u,
                    climatisationInfo: {
                      id: savedClimatisationInfo.id,
                      connectedTo: savedClimatisationInfo.connectedTo,
                      source: savedClimatisationInfo.source,
                      remarks: savedClimatisationInfo.remarks
                    }
                  }
                : u
            )
          );
          
          // Also update filtered units
          setFilteredUnits(prevUnits => 
            prevUnits.map(u => 
              u.id === targetUnitId 
                ? {
                    ...u,
                    climatisationInfo: {
                      id: savedClimatisationInfo.id,
                      connectedTo: savedClimatisationInfo.connectedTo,
                      source: savedClimatisationInfo.source,
                      remarks: savedClimatisationInfo.remarks
                    }
                  }
                : u
            )
          );
        }
      }

      setCopyModalOpen(false);
      setSelectedTargetUnits([]);
    } catch (error) {
      console.error("Fout bij kopiëren:", error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleShowHistory = (unit: ClimatisationUnit) => {
    setHistoryUnit(unit);
    setHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryUnit(null);
  };

  const getAllMaintenanceAttests = (unit: ClimatisationUnit) => {
    const allAttests = [...unit.maintenanceAttests, ...unit.devices.flatMap(d => d.maintenanceAttests)];
    // Sort by upload date (createdAt) descending (most recent first)
    return allAttests.sort((a, b) => {
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : (a.documentDate ? new Date(a.documentDate).getTime() : 0);
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : (b.documentDate ? new Date(b.documentDate).getTime() : 0);
      return bDate - aDate;
    });
  };

  const getExpirationStatus = (expirationDate?: string) => {
    if (!expirationDate) return { status: 'unknown', color: '#838C89' };
    
    const today = new Date();
    const expDate = new Date(expirationDate);
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);
    
    if (expDate < today) {
      return { status: 'expired', color: '#EA8162', text: 'Verlopen' }; // brick-primary
    } else if (expDate <= sixMonthsFromNow) {
      return { status: 'warning', color: '#EA8162', text: 'Verloopt binnenkort' }; // orange/brick-primary 
    } else {
      return { status: 'valid', color: '#5B7304', text: 'Geldig' }; // moss-primary
    }
  };

  const getFileStatus = (status?: string) => {
    if (!status) return { status: 'unknown', color: '#838C89', text: 'Onbekend' };
    
    switch (status) {
      case FileLogStatus.OK:
        return { status: 'valid', color: '#5B7304', text: 'Conform' }; // moss-primary
      case FileLogStatus.OK_WITH_REMARKS:
        return { status: 'warning', color: '#EA8162', text: 'Conform, met opmerkingen' }; // brick-primary
      case FileLogStatus.NOT_OK:
        return { status: 'expired', color: '#EA8162', text: 'Niet conform' }; // brick-primary
      case FileLogStatus.NOT_REQUIRED:
        return { status: 'not_required', color: '#838C89', text: 'Niet vereist' }; // neutral-secondaryMid
      case FileLogStatus.UNKNOWN:
        return { status: 'unknown', color: '#838C89', text: 'Onbekend' }; // neutral-secondaryMid
      default:
        return { status: 'unknown', color: '#838C89', text: 'Onbekend' };
    }
  };

  const getOverallWorstCaseStatus = (fileStatus?: string, expirationDate?: string) => {
    const fileStatusInfo = getFileStatus(fileStatus);
    const expirationStatusInfo = getExpirationStatus(expirationDate);
    
    // Priority: expired > warning > valid > not_required > unknown
    const statusPriority: { [key: string]: number } = {
      'expired': 4,
      'warning': 3,
      'valid': 2,
      'not_required': 1,
      'unknown': 0
    };
    
    const fileStatusPriority = statusPriority[fileStatusInfo.status] || 0;
    const expirationStatusPriority = statusPriority[expirationStatusInfo.status] || 0;
    
    // Return the worst case status
    if (fileStatusPriority >= expirationStatusPriority) {
      return fileStatusInfo;
    } else {
      return expirationStatusInfo;
    }
  };

  const renderEditableCell = (unit: ClimatisationUnit, field: string, value: string, options?: string[]) => {
    const isEditing = editingCell?.unitId === unit.id && editingCell?.field === field;

    if (isEditing) {
      return (
        <div className="flex items-center gap-2">
          {options ? (
            <Select
              size="sm"
              selectedKeys={editingValue ? [editingValue] : []}
              onSelectionChange={(keys) => setEditingValue(Array.from(keys)[0] as string || '')}
              className="min-w-32"
            >
              {options.map((option) => (
                <SelectItem key={option}>
                  {option}
                </SelectItem>
              ))}
            </Select>
          ) : (
            <Input
              size="sm"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              className="min-w-32"
            />
          )}
          <Button
            size="sm"
            isIconOnly
            color="success"
            variant="flat"
            onClick={handleEditSave}
          >
            <Check size={16} />
          </Button>
          <Button
            size="sm"
            isIconOnly
            color="danger"
            variant="flat"
            onClick={handleEditCancel}
          >
            <X size={16} />
          </Button>
        </div>
      );
    }

    const fieldLabels: Record<string, string> = {
      connectedTo: 'Aansluiting',
      source: 'Energiebron'
    };

    return (
      <div className="flex items-center gap-2 group">
        <span className="text-sm">{value || 'Onbekend'}</span>
        <Button
          size="sm"
          isIconOnly
          variant="light"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => handleEditStart(unit.id, field, value)}
        >
          <PencilSimple size={14} />
        </Button>
        <Button
          size="sm"
          isIconOnly
          variant="light"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => handleCopyField(unit, field, fieldLabels[field] || field)}
          title={`Kopieer ${fieldLabels[field]} naar andere units`}
        >
          <Copy size={14} />
        </Button>
      </div>
    );
  };

  const getConnectionOptions = () => Object.values(HeatingConnectedTo);

  const getSourceOptions = () => Object.values(ClimatisationSource);

  if (isLoading) {
    return <div className="flex justify-center p-4"><Spinner /></div>;
  }

  const actionCards = getActionCards();

  return (
    <div className="p-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-neutral-primary mb-2">
          Verwarmingsoverzicht
        </h3>
        <p className="text-sm text-neutral-secondary-mid mb-4">
          Overzicht van alle units en hun verwarmingssystemen. Gas-gedreven systemen vereisen een onderhoudsattest.
        </p>

        {/* Search Bar */}
        <div className="mb-6">
          <Input
            placeholder="Zoek op unit ID, bus nummer of gebouw..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startContent={<MagnifyingGlass size={20} className="text-neutral-secondary-mid" />}
            size="sm"
            className="max-w-md"
            isClearable
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
          {/* Gas systemen */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'gas' 
                ? 'border-brick-primary bg-red-50' 
                : 'border-red-500/30 hover:bg-red-50 hover:border-red-500/50'
            }`}
            onClick={() => handleActionCardClick('gas')}
          >
            <div className="flex items-center justify-center mb-2">
              <FireExtinguisher size={24} className="text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-500 mb-1">
              {actionCards.find(card => card.filterType === 'gas')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Gas systemen
            </div>
          </div>

          {/* Elektrische systemen */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'electricity' 
                ? 'border-brick-primary bg-green-50' 
                : 'border-green-500/30 hover:bg-green-50 hover:border-green-500/50'
            }`}
            onClick={() => handleActionCardClick('electricity')}
          >
            <div className="flex items-center justify-center mb-2">
              <Lightbulb size={24} className="text-green-500" />
            </div>
            <div className="text-2xl font-bold text-green-500 mb-1">
              {actionCards.find(card => card.filterType === 'electricity')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Elektrische systemen
            </div>
          </div>

          {/* Gas zonder geldig attest */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'gas_no_valid_attest' 
                ? 'border-brick-primary bg-red-50' 
                : 'border-red-500/30 hover:bg-red-50 hover:border-red-500/50'
            }`}
            onClick={() => handleActionCardClick('gas_no_valid_attest')}
          >
            <div className="flex items-center justify-center mb-2">
              <Warning size={24} className="text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-500 mb-1">
              {actionCards.find(card => card.filterType === 'gas_no_valid_attest')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Gas zonder geldig attest
            </div>
          </div>

          {/* Ontbrekende info over aansluiting */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'missing_connection_info' 
                ? 'border-brick-primary bg-gray-50' 
                : 'border-neutral-secondary-mid/30 hover:bg-gray-50 hover:border-neutral-secondary-mid/50'
            }`}
            onClick={() => handleActionCardClick('missing_connection_info')}
          >
            <div className="flex items-center justify-center mb-2">
              <Question size={24} className="text-neutral-secondary-mid" />
            </div>
            <div className="text-2xl font-bold text-neutral-secondary-mid mb-1">
              {actionCards.find(card => card.filterType === 'missing_connection_info')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Ontbrekende info over aansluiting
            </div>
          </div>

          {/* Ontbrekende info over energiebron */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'missing_energy_source_info' 
                ? 'border-brick-primary bg-gray-50' 
                : 'border-neutral-secondary-mid/30 hover:bg-gray-50 hover:border-neutral-secondary-mid/50'
            }`}
            onClick={() => handleActionCardClick('missing_energy_source_info')}
          >
            <div className="flex items-center justify-center mb-2">
              <Question size={24} className="text-neutral-secondary-mid" />
            </div>
            <div className="text-2xl font-bold text-neutral-secondary-mid mb-1">
              {actionCards.find(card => card.filterType === 'missing_energy_source_info')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Ontbrekende info over energiebron
            </div>
          </div>

          {/* Verlopen keuringen */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'expired' 
                ? 'border-brick-primary bg-red-50' 
                : 'border-red-500/30 hover:bg-red-50 hover:border-red-500/50'
            }`}
            onClick={() => handleActionCardClick('expired')}
          >
            <div className="flex items-center justify-center mb-2">
              <Calendar size={24} className="text-red-500" />
            </div>
            <div className="text-2xl font-bold text-red-500 mb-1">
              {actionCards.find(card => card.filterType === 'expired')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Verlopen keuringen
            </div>
          </div>

          {/* Binnenkort vervallen */}
          <div 
            className={`bg-white border rounded-lg p-4 text-center cursor-pointer transition-all duration-200 ${
              activeActionCard === 'warning' 
                ? 'border-brick-primary bg-yellow-50' 
                : 'border-yellow-500/30 hover:bg-yellow-50 hover:border-yellow-500/50'
            }`}
            onClick={() => handleActionCardClick('warning')}
          >
            <div className="flex items-center justify-center mb-2">
              <Calendar size={24} className="text-yellow-500" />
            </div>
            <div className="text-2xl font-bold text-yellow-500 mb-1">
              {actionCards.find(card => card.filterType === 'warning')?.count || 0}
            </div>
            <div className="text-sm text-neutral-secondary-mid">
              Binnenkort vervallen
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-medium text-neutral-primary">Filters</h4>
            {hasActiveFilters && (
              <Button
                size="sm"
                variant="flat"
                color="danger"
                onClick={clearAllFilters}
              >
                Alle filters wissen
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Select
              label="Toesteltype"
              placeholder="Alle types"
              selectedKeys={filters.deviceType ? [filters.deviceType] : []}
              onSelectionChange={(keys) => handleFilterChange('deviceType', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              {getUniqueDeviceTypes().map((type) => (
                <SelectItem key={type}>
                  {type}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Energiebron toestel"
              placeholder="Alle bronnen"
              selectedKeys={filters.deviceEnergySource ? [filters.deviceEnergySource] : []}
              onSelectionChange={(keys) => handleFilterChange('deviceEnergySource', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              <SelectItem key="Gas">Gas</SelectItem>
              <SelectItem key="Elektriciteit">Elektriciteit</SelectItem>
              <SelectItem key="Hybride">Hybride</SelectItem>
            </Select>

            <Select
              label="Toestel drijft"
              placeholder="Alle functies"
              selectedKeys={filters.deviceDrives ? [filters.deviceDrives] : []}
              onSelectionChange={(keys) => handleFilterChange('deviceDrives', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              {getUniqueDeviceDrives().map((drive) => (
                <SelectItem key={drive}>
                  {drive}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Aansluiting"
              placeholder="Alle aansluitingen"
              selectedKeys={filters.climatisationConnectedTo ? [filters.climatisationConnectedTo] : []}
              onSelectionChange={(keys) => handleFilterChange('climatisationConnectedTo', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              {getUniqueConnectionTypes().map((type) => (
                <SelectItem key={type}>
                  {type}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Energiebron systeem"
              placeholder="Alle bronnen"
              selectedKeys={filters.climatisationSource ? [filters.climatisationSource] : []}
              onSelectionChange={(keys) => handleFilterChange('climatisationSource', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              <SelectItem key="Gas">Gas</SelectItem>
              <SelectItem key="Elektriciteit">Elektriciteit</SelectItem>
            </Select>

            <Select
              label="Onderhoudsstatus"
              placeholder="Alle statussen"
              selectedKeys={filters.maintenanceStatus ? [filters.maintenanceStatus] : []}
              onSelectionChange={(keys) => handleFilterChange('maintenanceStatus', Array.from(keys)[0] as string || '')}
              size="sm"
            >
              <SelectItem key="valid">Geldig</SelectItem>
              <SelectItem key="warning">Verloopt binnenkort</SelectItem>
              <SelectItem key="expired">Verlopen</SelectItem>
              <SelectItem key="missing">Ontbreekt</SelectItem>
              <SelectItem key="not_required">Niet vereist</SelectItem>
            </Select>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mb-4 p-3 bg-brick-secondary-fine rounded-lg">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-neutral-primary">Actieve filters:</span>
              {searchQuery.trim() && (
                <Chip
                  size="sm"
                  className="bg-brick-primary text-white"
                  onClose={() => setSearchQuery('')}
                >
                  Zoeken: {searchQuery}
                </Chip>
              )}
              {Object.entries(filters).map(([key, value]) => {
                if (!value) return null;
                const filterLabels: Record<string, string> = {
                  deviceType: 'Toesteltype',
                  deviceEnergySource: 'Energiebron toestel',
                  deviceDrives: 'Toestel drijft',
                  climatisationConnectedTo: 'Aansluiting',
                  climatisationSource: 'Energiebron systeem',
                  maintenanceStatus: 'Onderhoudsstatus'
                };
                return (
                  <Chip
                    key={key}
                    size="sm"
                    className="bg-brick-primary text-white"
                    onClose={() => handleFilterChange(key as keyof Filters, '')}
                  >
                    {filterLabels[key]}: {value}
                  </Chip>
                );
              })}
              <span className="text-sm text-neutral-secondary-mid">
                ({filteredUnits.length} van {units.length} units)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Header */}
      {isHeaderSticky && (
        <div 
          className="fixed top-16 bg-white shadow-md z-40 border-b"
          style={{ 
            width: tableWidth,
            left: tableLeft
          }}
        >
          <div className="flex">
            {['Unit', 'Verdieping', 'Aansluiting', 'Energiebron', 'Opwekkingstoestel', 'Afgiftetoestel', 'Status', 'Laatste keuring', 'Volgende keuring'].map((header, index) => (
              <div 
                key={header}
                className="p-3 font-medium text-neutral-primary border-r border-gray-200 last:border-r-0"
                style={{ width: columnWidths[index] || 'auto' }}
              >
                {header}
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={tableRef}>
        <Table aria-label="Verwarmingsoverzicht per unit">
          <TableHeader>
            <TableColumn>Unit</TableColumn>
            <TableColumn>Verdieping</TableColumn>
            <TableColumn>Aansluiting</TableColumn>
            <TableColumn>Energiebron</TableColumn>
            <TableColumn>Opwekkingstoestel</TableColumn>
            <TableColumn>Afgiftetoestel</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Laatste keuring</TableColumn>
            <TableColumn>Volgende keuring</TableColumn>
          </TableHeader>
          <TableBody>
            {filteredUnits.map((unit) => {
              const maintenanceInfo = getMaintenanceInfo(unit);
              return (
                <TableRow key={unit.id}>
                  <TableCell>
                    <div className="font-medium">{unit.unit.id}</div>
                    {unit.unit.boxNumber && (
                      <div className="text-xs text-neutral-secondary-mid">
                        Bus {unit.unit.boxNumber}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{unit.unit.floor}</TableCell>
                  <TableCell>
                    {renderEditableCell(
                      unit, 
                      'connectedTo', 
                      getConnectionTypeText(unit.climatisationInfo?.connectedTo),
                      getConnectionOptions()
                    )}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(
                      unit, 
                      'source', 
                      getSourceText(unit.climatisationInfo?.source),
                      getSourceOptions()
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm max-w-48">
                      {getDevicesByType(unit, 'Opwekkingstoestel')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm max-w-48">
                      {getDevicesByType(unit, 'Afgiftetoestel')}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: getStatusColor(maintenanceInfo.status) }}
                      />
                      <span className="text-sm">{maintenanceInfo.text}</span>
                      {maintenanceInfo.sharePointFileUrl && (
                        <a 
                          href={maintenanceInfo.sharePointFileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-brick-primary hover:text-brick-primary/80 transition-colors"
                        >
                          <FilePdf size={16} weight="fill" />
                        </a>
                      )}
                      {shouldShowUploadButton(unit) && (
                        <Chip
                          size="sm"
                          className="bg-brick-primary text-white cursor-pointer"
                          startContent={<Plus size={14} />}
                          onClick={() => handleUploadAttest(unit)}
                        >
                          Upload attest
                        </Chip>
                      )}
                      {getAllMaintenanceAttests(unit).length > 0 && (
                        <Button
                          size="sm"
                          isIconOnly
                          variant="light"
                          className="text-neutral-secondary-mid hover:text-neutral-primary transition-colors"
                          onClick={() => handleShowHistory(unit)}
                          title="Bekijk historie van onderhoudsattesten"
                        >
                          <Clock size={16} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {formatDate(maintenanceInfo.documentDate)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {formatDate(maintenanceInfo.expirationDate)}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filteredUnits.length === 0 && !isLoading && (
        <div className="text-center py-8 text-neutral-secondary-mid">
          {hasActiveFilters ? 'Geen units gevonden voor de geselecteerde filters' : 'Geen verwarmingsgegevens beschikbaar'}
        </div>
      )}

      {/* Upload Modal */}
      {selectedUnit && (
        <ModalUploadFileLog
          isOpen={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onFileUploadSuccess={handleFileUploadSuccess}
          unit={{ id: selectedUnit.unit.id }}
          building={{ id: selectedUnit.building.id }}
          skipFields={selectedUnit.climatisationInfo?.connectedTo === 'Centraal systeem' || 
                     selectedUnit.climatisationInfo?.connectedTo === 'Combinatie privatief-collectief' 
                     ? [FileUploadField.UNIT] 
                     : [FileUploadField.BUILDING]}
          defaultFileLabel={getUploadLabel(selectedUnit.climatisationInfo?.connectedTo === 'Centraal systeem' || 
                                         selectedUnit.climatisationInfo?.connectedTo === 'Combinatie privatief-collectief')}
        />
      )}

      {/* Copy Modal */}
      <Modal isOpen={copyModalOpen} onClose={() => setCopyModalOpen(false)} size="2xl">
        <ModalContent>
          <ModalHeader>
            Kopieer {sourceFieldLabel}
          </ModalHeader>
          <ModalBody>
            {sourceUnit && (
              <div className="space-y-4">
                <div className="p-3 bg-brick-secondary-fine rounded-lg">
                  <p className="text-sm text-neutral-primary">
                    <strong>Bron:</strong> Unit {sourceUnit.unit.id} - {sourceFieldLabel}: {
                      sourceField === 'connectedTo' 
                        ? getConnectionTypeText(sourceUnit.climatisationInfo?.connectedTo)
                        : getSourceText(sourceUnit.climatisationInfo?.source)
                    }
                  </p>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-neutral-primary">
                      Selecteer de units waarnaar je deze waarde wilt kopiëren:
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="flat"
                        onClick={() => {
                          const sameBuilding = units.filter(unit => 
                            unit.id !== sourceUnit.id && 
                            unit.building.id === sourceUnit.building.id
                          );
                          setSelectedTargetUnits(sameBuilding.map(unit => unit.id));
                        }}
                      >
                        Alles selecteren
                      </Button>
                      <Button
                        size="sm"
                        variant="flat"
                        onClick={() => setSelectedTargetUnits([])}
                      >
                        Alles deselecteren
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {units
                      .filter(unit => unit.id !== sourceUnit.id && unit.building.id === sourceUnit.building.id)
                      .map(unit => (
                        <div key={unit.id} className="flex items-center space-x-2">
                          <Checkbox
                            isSelected={selectedTargetUnits.includes(unit.id)}
                            onValueChange={(checked) => {
                              if (checked) {
                                setSelectedTargetUnits(prev => [...prev, unit.id]);
                              } else {
                                setSelectedTargetUnits(prev => prev.filter(id => id !== unit.id));
                              }
                            }}
                          />
                          <span className="text-sm">
                            Unit {unit.unit.id} - Huidige waarde: {
                              sourceField === 'connectedTo' 
                                ? getConnectionTypeText(unit.climatisationInfo?.connectedTo)
                                : getSourceText(unit.climatisationInfo?.source)
                            }
                          </span>
                        </div>
                      ))}
                  </div>
                  {units.filter(unit => unit.id !== sourceUnit.id && unit.building.id === sourceUnit.building.id).length === 0 && (
                    <p className="text-sm text-neutral-secondary-mid text-center py-4">
                      Geen andere units in hetzelfde gebouw gevonden.
                    </p>
                  )}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setCopyModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              color="primary" 
              onPress={copyFieldToSelectedTargets}
              isLoading={isCopying}
              isDisabled={selectedTargetUnits.length === 0}
            >
              Kopiëren naar {selectedTargetUnits.length} unit(s)
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={historyModalOpen} onClose={handleCloseHistoryModal} size="2xl">
        <ModalContent>
          <ModalHeader>
            Onderhoudsattesten Historie - Unit {historyUnit?.unit.id}
          </ModalHeader>
          <ModalBody>
            {historyUnit && (
              <div className="space-y-4">
                <div className="p-3 bg-brick-secondary-fine rounded-lg">
                  <p className="text-sm text-neutral-primary">
                    <strong>Unit:</strong> {historyUnit.unit.id} 
                    {historyUnit.unit.boxNumber && ` - Bus ${historyUnit.unit.boxNumber}`}
                  </p>
                  <p className="text-sm text-neutral-secondary-mid">
                    <strong>Gebouw:</strong> {historyUnit.building.id}
                  </p>
                  <p className="text-sm text-neutral-secondary-mid">
                    <strong>Energiebron:</strong> {getSourceText(historyUnit.climatisationInfo?.source)}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-md font-medium text-neutral-primary mb-3">
                    Alle onderhoudsattesten ({getAllMaintenanceAttests(historyUnit).length} totaal)
                  </h4>
                  
                  {getAllMaintenanceAttests(historyUnit).length === 0 ? (
                    <p className="text-sm text-neutral-secondary-mid text-center py-4">
                      Geen onderhoudsattesten gevonden voor deze unit.
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {getAllMaintenanceAttests(historyUnit).map((attest, index) => {
                        const overallStatus = getOverallWorstCaseStatus(attest.status, attest.expirationDate);
                        const fileStatusInfo = getFileStatus(attest.status);
                        const expirationStatusInfo = getExpirationStatus(attest.expirationDate);
                        
                        return (
                        <div 
                          key={attest.id} 
                          className={`p-4 border rounded-lg ${
                            index === 0 
                              ? 'border-moss-primary bg-green-50' 
                              : 'border-neutral-secondary-mid/30 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3">
                                {index === 0 && (
                                  <Chip size="sm" className="bg-moss-primary text-white">
                                    Meest Recent
                                  </Chip>
                                )}
                                {/* Overall worst-case status */}
                                <div 
                                  className="w-4 h-4 rounded-full shrink-0 border-2 border-white shadow-sm"
                                  style={{ backgroundColor: overallStatus.color }}
                                  title="Algemene status (worst case)"
                                />
                                <span className="text-sm font-medium">
                                  {overallStatus.text}
                                </span>
                              </div>

                              {/* Detailed status breakdown with two dots */}
                              <div className="flex items-center gap-4 mb-3 ml-1">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full shrink-0"
                                    style={{ backgroundColor: fileStatusInfo.color }}
                                    title="Bestand status"
                                  />
                                  <span className="text-xs text-neutral-secondary-mid">
                                    Bestand: {fileStatusInfo.text}
                                  </span>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full shrink-0"
                                    style={{ backgroundColor: expirationStatusInfo.color }}
                                    title="Vervaldatum status"
                                  />
                                  <span className="text-xs text-neutral-secondary-mid">
                                    Vervaldatum: {expirationStatusInfo.text || 'Geen datum'}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <strong>Document datum:</strong> {formatDate(attest.documentDate)}
                                </div>
                                <div>
                                  <strong>Vervaldatum:</strong> {formatDate(attest.expirationDate)}
                                </div>
                                <div>
                                  <strong>Geüpload op:</strong> {
                                    attest.createdAt 
                                      ? formatDate(attest.createdAt)
                                      : 'Onbekend'
                                  }
                                </div>
                                <div>
                                  <strong>Type:</strong> {
                                    attest.isBuildingLevel ? 'Collectief' : 'Unit-niveau'
                                  }
                                </div>
                              </div>
                              
                              {attest.statusDescription && (
                                <div className="mt-2">
                                  <strong className="text-sm">Opmerking:</strong>
                                  <p className="text-sm text-neutral-secondary-mid">
                                    {attest.statusDescription}
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            <div className="ml-4">
                              {attest.sharePointFileUrl && (
                                <a 
                                  href={attest.sharePointFileUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-brick-primary hover:text-brick-primary/80 transition-colors"
                                  title="Open document"
                                >
                                  <FilePdf size={24} weight="fill" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCloseHistoryModal}>
              Sluiten
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ClimatisationSection; 